/**
 * L'appel du matin sur le téléphone de l'animateur : ce que la page d'un lien
 * de présences sait sans la base.
 *
 * Un lien vaut pour un centre et une semaine (migration
 * `20261024230000_les_liens_de_presences`). La page lit la liste d'appel par
 * `presences_lien_lire`, pointe par `presences_lien_pointer`, et c'est la
 * base qui décide de tout ce qui compte : le lien est-il vivant, l'enfant
 * est-il bien de ce centre, ce jour est-il férié. Ici : la forme des données,
 * le jour à ouvrir, la recherche, les groupes, les comptes, et les textes.
 */
import { attenteDuRefus, messageDAttente } from '../liens-codes';

export const JOURS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
export type JourCle = (typeof JOURS)[number];

export const NOM_DU_JOUR: Record<JourCle, string> = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
};

export const NOM_COURT_DU_JOUR: Record<JourCle, string> = {
  monday: 'Lun',
  tuesday: 'Mar',
  wednesday: 'Mer',
  thursday: 'Jeu',
  friday: 'Ven',
};

export interface JourDuLien {
  cle: JourCle;
  /** `AAAA-MM-JJ`. */
  date: string;
  /** Le nom du jour férié, ou `null`. */
  ferie: string | null;
  /** Aucune session du centre ce jour-là (semaine de quatre jours). */
  sansSession: boolean;
}

export interface PresenceDeLEnfant {
  monday: boolean | null;
  tuesday: boolean | null;
  wednesday: boolean | null;
  thursday: boolean | null;
  friday: boolean | null;
  notes: string | null;
}

export interface EnfantDuLien {
  registrationId: string;
  prenom: string;
  nom: string;
  stage: string;
  sousGroupe: string | null;
  /** `null` tant que personne n'a rien pointé pour cet enfant. */
  presence: PresenceDeLEnfant | null;
}

export interface ListeDAppel {
  centre: { id: string; nom: string };
  semaine: { debut: string; fin: string; libelle: string };
  jours: JourDuLien[];
  enfants: EnfantDuLien[];
}

export type LectureDuLien = { codeRequis: true } | { codeRequis: false; liste: ListeDAppel };

export type EtatDuJour = 'present' | 'absent' | 'a_pointer';

/* ------------------------------------------------------------------------ */
/*  Lecture de ce que rend la base                                           */
/* ------------------------------------------------------------------------ */

interface PresenceLue {
  monday?: boolean | null;
  tuesday?: boolean | null;
  wednesday?: boolean | null;
  thursday?: boolean | null;
  friday?: boolean | null;
  notes?: string | null;
}

interface EnfantLu {
  registration_id: string;
  prenom: string | null;
  nom: string | null;
  stage: string | null;
  sous_groupe: string | null;
  presence: PresenceLue | null;
}

interface JourLu {
  cle: JourCle;
  date: string;
  ferie: string | null;
  sans_session: boolean;
}

interface ReponseLue {
  code_requis?: boolean;
  centre?: { id: string; nom: string };
  semaine?: { debut: string; fin: string; libelle: string };
  jours?: JourLu[];
  enfants?: EnfantLu[];
}

/** La présence telle que la base la rend (`_lien_presences_json`). */
export function lirePresence(p: PresenceLue | null | undefined): PresenceDeLEnfant | null {
  if (!p) return null;
  return {
    monday: p.monday ?? null,
    tuesday: p.tuesday ?? null,
    wednesday: p.wednesday ?? null,
    thursday: p.thursday ?? null,
    friday: p.friday ?? null,
    notes: p.notes ?? null,
  };
}

/** Ce que rend `presences_lien_lire`. */
export function lireReponse(brut: unknown): LectureDuLien {
  const r = (brut ?? {}) as ReponseLue;
  if (r.code_requis || !r.centre || !r.semaine) return { codeRequis: true };
  return {
    codeRequis: false,
    liste: {
      centre: { id: r.centre.id, nom: r.centre.nom },
      semaine: { debut: r.semaine.debut, fin: r.semaine.fin, libelle: r.semaine.libelle },
      jours: (r.jours ?? []).map((j) => ({
        cle: j.cle,
        date: j.date,
        ferie: j.ferie ?? null,
        sansSession: Boolean(j.sans_session),
      })),
      enfants: (r.enfants ?? []).map((e) => ({
        registrationId: e.registration_id,
        prenom: e.prenom ?? '',
        nom: e.nom ?? '',
        stage: e.stage ?? '',
        sousGroupe: e.sous_groupe ?? null,
        presence: lirePresence(e.presence),
      })),
    },
  };
}

/* ------------------------------------------------------------------------ */
/*  Le jour                                                                  */
/* ------------------------------------------------------------------------ */

/** Un jour où l'appel se fait : ni férié, ni sans session. */
export function jourPointable(j: JourDuLien): boolean {
  return !j.ferie && !j.sansSession;
}

/**
 * Le jour que la page ouvre : aujourd'hui s'il est dans la semaine et qu'on y
 * fait l'appel ; sinon le premier jour d'appel à venir ; sinon le dernier (la
 * semaine est passée, on relit). `null` sans aucun jour d'appel.
 */
export function jourParDefaut(jours: readonly JourDuLien[], aujourdHui: string): JourCle | null {
  const pointables = jours.filter(jourPointable);
  if (pointables.length === 0) return null;
  const duJour = pointables.find((j) => j.date === aujourdHui);
  if (duJour) return duJour.cle;
  const aVenir = pointables.find((j) => j.date > aujourdHui);
  return (aVenir ?? pointables[pointables.length - 1]).cle;
}

/** L'état d'un enfant pour un jour. */
export function etatDuJour(enfant: EnfantDuLien, jour: JourCle): EtatDuJour {
  const v = enfant.presence?.[jour];
  if (v === true) return 'present';
  if (v === false) return 'absent';
  return 'a_pointer';
}

export interface ComptesDuJour {
  presents: number;
  absents: number;
  aPointer: number;
}

export function compterLeJour(enfants: readonly EnfantDuLien[], jour: JourCle): ComptesDuJour {
  const c: ComptesDuJour = { presents: 0, absents: 0, aPointer: 0 };
  for (const e of enfants) {
    const etat = etatDuJour(e, jour);
    if (etat === 'present') c.presents += 1;
    else if (etat === 'absent') c.absents += 1;
    else c.aPointer += 1;
  }
  return c;
}

/* ------------------------------------------------------------------------ */
/*  La liste                                                                 */
/* ------------------------------------------------------------------------ */

/** Sans accents, sans ponctuation, en minuscules. */
function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** La recherche : sur le prénom, le nom, le stage et le sous-groupe, sans accents. */
export function filtrerEnfants(enfants: readonly EnfantDuLien[], recherche: string): EnfantDuLien[] {
  const q = normaliser(recherche);
  if (!q) return [...enfants];
  const mots = q.split(' ');
  return enfants.filter((e) => {
    const texte = normaliser(`${e.prenom} ${e.nom} ${e.stage} ${e.sousGroupe ?? ''}`);
    return mots.every((m) => texte.includes(m));
  });
}

export interface GroupeDAppel {
  /** Le stage, puis le sous-groupe s'il y en a un : « Les Colibris · Groupe A ». */
  titre: string;
  stage: string;
  sousGroupe: string | null;
  enfants: EnfantDuLien[];
}

/** Par stage puis sous-groupe, dans l'ordre alphabétique ; les enfants par prénom. */
export function grouperParStage(enfants: readonly EnfantDuLien[]): GroupeDAppel[] {
  const parCle = new Map<string, GroupeDAppel>();
  for (const e of enfants) {
    const cle = `${e.stage}\u0000${e.sousGroupe ?? ''}`;
    let g = parCle.get(cle);
    if (!g) {
      g = {
        titre: e.sousGroupe ? `${e.stage} · ${e.sousGroupe}` : e.stage,
        stage: e.stage,
        sousGroupe: e.sousGroupe,
        enfants: [],
      };
      parCle.set(cle, g);
    }
    g.enfants.push(e);
  }
  const groupes = [...parCle.values()];
  for (const g of groupes) {
    g.enfants.sort(
      (a, b) => a.prenom.localeCompare(b.prenom, 'fr') || a.nom.localeCompare(b.nom, 'fr'),
    );
  }
  return groupes.sort(
    (a, b) =>
      a.stage.localeCompare(b.stage, 'fr') ||
      (a.sousGroupe ?? '').localeCompare(b.sousGroupe ?? '', 'fr'),
  );
}

/** La liste après un geste, sans attendre la base. */
export function appliquerPointage(
  enfants: readonly EnfantDuLien[],
  registrationId: string,
  jour: JourCle,
  present: boolean | null,
): EnfantDuLien[] {
  return enfants.map((e) =>
    e.registrationId === registrationId
      ? { ...e, presence: { ...(e.presence ?? PRESENCE_VIDE), [jour]: present } }
      : e,
  );
}

/** La liste après une note. */
export function appliquerNote(
  enfants: readonly EnfantDuLien[],
  registrationId: string,
  notes: string | null,
): EnfantDuLien[] {
  const propre = notes?.trim() ? notes.trim() : null;
  return enfants.map((e) =>
    e.registrationId === registrationId
      ? { ...e, presence: { ...(e.presence ?? PRESENCE_VIDE), notes: propre } }
      : e,
  );
}

/** La liste après ce que la base a réellement enregistré (ou remise comme avant : `null` compris). */
export function remplacerPresence(
  enfants: readonly EnfantDuLien[],
  registrationId: string,
  presence: PresenceDeLEnfant | null,
): EnfantDuLien[] {
  return enfants.map((e) => (e.registrationId === registrationId ? { ...e, presence } : e));
}

export const PRESENCE_VIDE: PresenceDeLEnfant = {
  monday: null,
  tuesday: null,
  wednesday: null,
  thursday: null,
  friday: null,
  notes: null,
};

/**
 * Un tap sur la case fait tourner l'état (décidé le 24/10/2026) : à pointer →
 * présent → absent → à pointer.
 */
export function prochainEtat(courant: EtatDuJour): boolean | null {
  if (courant === 'a_pointer') return true;
  if (courant === 'present') return false;
  return null;
}

/**
 * La clé d'un groupe dans l'adresse : son titre en base64url.
 *
 * Pas `encodeURIComponent` : le routeur décode l'adresse avant de la rendre,
 * et « Vélo (6/10 ans) » redevenait « Vélo (6/10 ans) » — avec sa barre, que
 * la lecture de l'adresse prenait pour une séparation (vu le 25/10/2026 à
 * Auderghem). Le base64url ne contient que des lettres, des chiffres, `-` et
 * `_` : rien que le routeur ou le navigateur touche.
 */
export function cleDeGroupe(groupe: Pick<GroupeDAppel, 'titre'>): string {
  const octets = new TextEncoder().encode(groupe.titre);
  let binaire = '';
  for (const o of octets) binaire += String.fromCharCode(o);
  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Le titre derrière une clé d'adresse, ou `null` si elle est cassée. */
export function titreDeLaCle(cle: string): string | null {
  if (!/^[A-Za-z0-9_-]+$/.test(cle)) return null;
  try {
    const b64 = cle.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (cle.length % 4)) % 4);
    const binaire = atob(b64);
    return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(binaire, (c) => c.charCodeAt(0)));
  } catch {
    return null;
  }
}

export function trouverGroupe(groupes: readonly GroupeDAppel[], cle: string): GroupeDAppel | null {
  const titre = titreDeLaCle(cle);
  if (titre === null) return null;
  return groupes.find((g) => g.titre === titre) ?? null;
}

/* ------------------------------------------------------------------------ */
/*  La fiche de l'enfant                                                     */
/* ------------------------------------------------------------------------ */

export interface PersonneAutorisee {
  prenom: string;
  nom: string;
  telephone: string | null;
  lien: string | null;
}

export interface FicheEnfant {
  registrationId: string;
  stage: string;
  sousGroupe: string | null;
  prenom: string;
  nom: string;
  dateNaissance: string | null;
  ecole: string | null;
  parent: { prenom: string; nom: string; telephone: string | null; telephone2: string | null };
  depart: { partSeul: boolean | null; heure: string | null; personnes: PersonneAutorisee[] };
  sante: {
    allergies: boolean | null;
    allergiesDetails: string | null;
    allergiesConsequences: string | null;
    regime: boolean | null;
    regimeDetails: string | null;
    medicaments: boolean | null;
    medicamentsDetails: string | null;
    medicamentsAutonomie: boolean | null;
    aSavoir: string | null;
    antecedents: string | null;
    tetanos: boolean | null;
    medecinNom: string | null;
    medecinTelephone: string | null;
  };
  activites: {
    participe: boolean | null;
    restrictions: string | null;
    nage: boolean | null;
    niveauNage: string | null;
    peurEau: boolean | null;
    autres: string | null;
  };
  /** `null` quand la famille n'a pas signalé de besoin particulier. */
  inclusion: {
    situation: string | null;
    impact: string | null;
    accompagnement: string | null;
    accompagnementDetails: string | null;
    strategies: string | null;
    aides: string | null;
    signauxStress: string | null;
    forces: string | null;
    experience: string | null;
  } | null;
  /** `null` tant que la famille n'a pas répondu sur le droit à l'image. */
  image: {
    espacesInternes: boolean;
    rapportsActivite: boolean;
    sitePublic: boolean;
    reseauxSociaux: boolean;
    imprimes: boolean;
    le: string | null;
  } | null;
}

export type LectureDeFiche = { codeRequis: true } | { codeRequis: false; fiche: FicheEnfant };

const texte = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
};
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

/** Ce que rend `presences_lien_enfant`. */
export function lireFiche(brut: unknown): LectureDeFiche {
  const r = (brut ?? {}) as Record<string, unknown>;
  if (r.code_requis || !r.enfant) return { codeRequis: true };
  const enfant = r.enfant as Record<string, unknown>;
  const parent = (r.parent ?? {}) as Record<string, unknown>;
  const depart = (r.depart ?? {}) as Record<string, unknown>;
  const sante = (r.sante ?? {}) as Record<string, unknown>;
  const activites = (r.activites ?? {}) as Record<string, unknown>;
  const inclusion = (r.inclusion ?? null) as Record<string, unknown> | null;
  const image = (r.image ?? null) as Record<string, unknown> | null;
  return {
    codeRequis: false,
    fiche: {
      registrationId: String(r.registration_id ?? ''),
      stage: texte(r.stage) ?? '',
      sousGroupe: texte(r.sous_groupe),
      prenom: texte(enfant.prenom) ?? '',
      nom: texte(enfant.nom) ?? '',
      dateNaissance: texte(enfant.date_naissance),
      ecole: texte(enfant.ecole),
      parent: {
        prenom: texte(parent.prenom) ?? '',
        nom: texte(parent.nom) ?? '',
        telephone: texte(parent.telephone),
        telephone2: texte(parent.telephone2),
      },
      depart: {
        partSeul: bool(depart.part_seul),
        heure: texte(depart.heure),
        personnes: (Array.isArray(depart.personnes) ? depart.personnes : []).map((p) => {
          const q = p as Record<string, unknown>;
          return {
            prenom: texte(q.prenom) ?? '',
            nom: texte(q.nom) ?? '',
            telephone: texte(q.telephone),
            lien: texte(q.lien),
          };
        }),
      },
      sante: {
        allergies: bool(sante.allergies),
        allergiesDetails: texte(sante.allergies_details),
        allergiesConsequences: texte(sante.allergies_consequences),
        regime: bool(sante.regime),
        regimeDetails: texte(sante.regime_details),
        medicaments: bool(sante.medicaments),
        medicamentsDetails: texte(sante.medicaments_details),
        medicamentsAutonomie: bool(sante.medicaments_autonomie),
        aSavoir: texte(sante.a_savoir),
        antecedents: texte(sante.antecedents),
        tetanos: bool(sante.tetanos),
        medecinNom: texte(sante.medecin_nom),
        medecinTelephone: texte(sante.medecin_telephone),
      },
      activites: {
        participe: bool(activites.participe),
        restrictions: texte(activites.restrictions),
        nage: bool(activites.nage),
        niveauNage: texte(activites.niveau_nage),
        peurEau: bool(activites.peur_eau),
        autres: texte(activites.autres),
      },
      inclusion: inclusion
        ? {
            situation: texte(inclusion.situation),
            impact: texte(inclusion.impact),
            accompagnement: texte(inclusion.accompagnement),
            accompagnementDetails: texte(inclusion.accompagnement_details),
            strategies: texte(inclusion.strategies),
            aides: texte(inclusion.aides),
            signauxStress: texte(inclusion.signaux_stress),
            forces: texte(inclusion.forces),
            experience: texte(inclusion.experience),
          }
        : null,
      image: image
        ? {
            espacesInternes: image.espaces_internes === true,
            rapportsActivite: image.rapports_activite === true,
            sitePublic: image.site_public === true,
            reseauxSociaux: image.reseaux_sociaux === true,
            imprimes: image.imprimes === true,
            le: texte(image.le),
          }
        : null,
    },
  };
}

/** « 7 ans » à une date donnée ; `null` sans date de naissance. */
export function ageEnClair(dateNaissance: string | null, aujourdHui: string): string | null {
  if (!dateNaissance) return null;
  const [an, mn, jn] = dateNaissance.split('-').map(Number);
  const [a, m, j] = aujourdHui.split('-').map(Number);
  let age = a - an;
  if (m < mn || (m === mn && j < jn)) age -= 1;
  if (age < 0 || Number.isNaN(age)) return null;
  return `${age} an${age > 1 ? 's' : ''}`;
}

/** « 15/03/2019 ». */
export function dateEnClair(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/** Ce qu'il faut voir d'un coup d'œil sur un enfant : les alertes de sa fiche. */
export function alertesDeLaFiche(f: FicheEnfant): string[] {
  const alertes: string[] = [];
  if (f.sante.allergies) alertes.push(`Allergies : ${f.sante.allergiesDetails ?? 'oui'}`);
  if (f.sante.regime) alertes.push(`Régime : ${f.sante.regimeDetails ?? 'oui'}`);
  if (f.sante.medicaments) alertes.push(`Médicaments : ${f.sante.medicamentsDetails ?? 'oui'}`);
  if (f.sante.aSavoir) alertes.push(f.sante.aSavoir);
  if (f.activites.participe === false) alertes.push(`Activités limitées${f.activites.restrictions ? ` : ${f.activites.restrictions}` : ''}`);
  if (f.inclusion) alertes.push('Besoins particuliers : voir la fiche');
  if (f.depart.partSeul) alertes.push(`Repart seul·e${f.depart.heure ? ` (${f.depart.heure})` : ''}`);
  return alertes;
}

/* ------------------------------------------------------------------------ */
/*  Les dates et les textes                                                  */
/* ------------------------------------------------------------------------ */

/** `AAAA-MM-JJ` du lundi de la semaine qui contient la date. */
export function lundiDe(date: string): string {
  const [a, m, j] = date.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j));
  const isodow = (d.getUTCDay() + 6) % 7; // lundi = 0
  d.setUTCDate(d.getUTCDate() - isodow);
  return d.toISOString().slice(0, 10);
}

/**
 * Le lundi que l'écran de gestion propose : celui de la semaine en cours du
 * lundi au vendredi, le prochain le week-end — le samedi, on prépare lundi.
 */
export function lundiPropose(aujourdHui: string): string {
  const lundi = lundiDe(aujourdHui);
  const [a, m, j] = aujourdHui.split('-').map(Number);
  const isodow = (new Date(Date.UTC(a, m - 1, j)).getUTCDay() + 6) % 7;
  if (isodow < 5) return lundi;
  const d = new Date(`${lundi}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 7);
  return d.toISOString().slice(0, 10);
}

/** « lun. 26/10 ». */
export function dateCourte(iso: string): string {
  const [a, m, j] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j));
  const jour = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'][d.getUTCDay()];
  return `${jour} ${String(j).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

/** « du 26/10 au 30/10 ». */
export function semaineEnClair(debut: string, fin: string): string {
  const jm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  return `du ${jm(debut)} au ${jm(fin)}`;
}

/** L'adresse publique d'un lien. */
export function adresseDuLien(origine: string, jeton: string): string {
  return `${origine.replace(/\/$/, '')}/presences/${jeton}`;
}

/** Le message à coller dans le groupe WhatsApp du centre. */
export function messagePourLesAnims(args: {
  centre: string;
  debut: string;
  fin: string;
  adresse: string;
  code: string | null;
}): string {
  const lignes = [
    `Présences ${args.centre}, semaine ${semaineEnClair(args.debut, args.fin)} :`,
    args.adresse,
    '',
    'Ouvrez ce lien sur votre téléphone pour faire l’appel du matin : ✓ présent, ✗ absent. Chaque geste s’enregistre tout seul.',
  ];
  if (args.code) lignes.push(`Code à l’ouverture : ${args.code}`);
  lignes.push('Le lien ne vaut que pour ce centre et cette semaine.');
  return lignes.join('\n');
}

/** Ce que la page dit quand la base refuse le lien. */
export function messageDeRefus(message: string): string {
  const attente = attenteDuRefus(message);
  if (attente !== null) return messageDAttente(attente);
  if (message.includes('lien_revoque')) {
    return 'Ce lien a été désactivé. Demandez le nouveau lien à la coordination.';
  }
  if (message.includes('lien_expire')) {
    return 'Ce lien ne vaut plus : la semaine est terminée.';
  }
  if (message.includes('lien_bloque')) {
    return 'Ce lien est bloqué après trop de codes faux. Demandez un nouveau lien à la coordination.';
  }
  if (message.includes('lien_inconnu')) {
    return 'Ce lien n’existe pas. Vérifiez l’adresse reçue, ou demandez-la à la coordination.';
  }
  if (message.includes('jour_ferie')) {
    return 'Ce jour est férié : pas d’appel.';
  }
  if (message.includes('inscription_hors_lien')) {
    return 'Cet enfant n’est plus dans la liste de ce centre. Rechargez la page.';
  }
  return 'Impossible pour le moment. Vérifiez la connexion et réessayez.';
}
